import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useMemo, useRef, useState } from 'react'

import { EXPERIMENTS } from '@src/experiments'
import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import {
  getInitialSelectedOptions,
  getProductOptionNamesFromProduct,
  getSelectedColorFromSelectedOptions,
  getSelectedOptionFromSelectedOptions,
  getSelectedSizeFromSelectedOptions,
  isNestedProduct,
} from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import Product from '@src/quince-site-common/src/domains/Product'
import Bundle from '@src/quince-site-common/src/domains/Product/BundleProduct'
import { ProductOptionName } from '@src/quince-site-common/src/domains/ProductOption'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import { useBuyNowPDP } from '@src/quince-site-common/src/hooks/cart/hooks/use-buy-now-pdp'
import useExperimentBucket from '@src/quince-site-common/src/hooks/useExperimentBucket'
import pdpStateManager from '@src/quince-site-common/src/redux/globalStateManager/PDPStateManager'
import { getFestivalOfferPrice } from '@src/quince-site-common/src/utils/festivalOffersUtility'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { useIsInMiniPdpExperiment } from '@src/quince-site-pdp/src/templates/productPage/productPage.utils'
import { TPDPSectionData } from '@src/quince-site-pdp/src/types/pdp-section-data.type'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import ProductPageSectionProps from '../../../domains/ProductPageSectionProps'
import ProductFormWrapper from '../../../molecules/productFormWrapper/ProductFormWrapper'

import {
  findExactOrContainingHandleGroupItem,
  getOptionBasedOnQueryString,
  updateOptionsInUrl,
} from './productPurchase.helpers'
import { useProductTracker } from './use-product-tracker'

const ProductPurchase = (props: ProductPageSectionProps) => {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const showBuyNow = useBuyNowPDP(props.product)

  const [selectedOptionsState, setSelectedOptionsState] = useState<ProductVariantOption[] | null>(null)

  const selectedOptions = useMemo(
    () => selectedOptionsState ?? getInitialSelectedOptionsForProduct(props.product, props.sectionData),
    [selectedOptionsState, props.product, props.sectionData],
  )

  const isStickyATCShowBucket =
    useExperimentBucket(EXPERIMENTS.STICKY_ATC_A.experimentName) === EXPERIMENTS.STICKY_ATC_A.bucketNames.show
  const isMiniPdpExperiment = useIsInMiniPdpExperiment()

  const variants = productHelper.getVariantsForOptions(props.product, selectedOptions)
  const variantId = variants[0]?.svid?.toString()
  const productProperties = getProductPropertiesForTracking(props.product, variantId)

  useProductTracker(props.product, selectedOptions, props.sectionData, productProperties)

  useEffect(() => {
    pdpStateManager.setSelectedOptions({
      slug: props.product.slug,
      spid: props.product.spid,
      selectedOptions: selectedOptions.map(so => ({ name: so.name, value: so.value })),
      productId: props.product.id.toString(),
      productTitle: props.product.title,
    })
    const salePrice = productHelper.getQuincePriceForOptions(props.product, selectedOptions)
    const festivalOfferPrice =
      getFestivalOfferPrice({
        product: props.product,
        selectedOptions,
      }) ?? undefined
    pdpStateManager.setSeoPrice({
      seoPrice: festivalOfferPrice ?? salePrice,
    })
  }, [selectedOptions])

  const didMountRef = useRef(false)
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true
    } else {
      updateOptionsInUrl(pathname, searchParams, selectedOptions, router)
    }
  }, [
    props.product.id,
    getSelectedColorFromSelectedOptions(selectedOptions),
    getSelectedSizeFromSelectedOptions(selectedOptions),
    getSelectedOptionFromSelectedOptions(selectedOptions),
  ])

  const selectedSiblingProductId = props.product.addOnProductOptionGroups?.[0]?.groupItems
    ? findExactOrContainingHandleGroupItem(props.product.addOnProductOptionGroups[0].groupItems, pathname)?.spid
    : undefined

  return (
    <ProductFormWrapper
      product={props.product}
      selectedOptions={selectedOptions}
      onSelectedOptionsChange={setSelectedOptionsState}
      onReviewSummaryClick={() => {
        pdpStateManager.setShowReviewSection({ reviewSectionInFocus: true })
        trackEvent(TELEMETRY_EVENT_NAMES.ENTER_FEEDBACK, {
          context: {
            productProperties,
          },
        })
      }}
      // @i18n-ignore
      addToCartTracker={'pdp_page'}
      selectedSiblingProductId={selectedSiblingProductId}
      preOrderModalData={props.preOrderModalData}
      showStickyATCNewDesign={isStickyATCShowBucket && !isMiniPdpExperiment}
      showBuyNow={showBuyNow}
    />
  )
}

const COLOR_QUERY_STRING = 'color'
const SIZE_QUERY_STRING = 'size'

export const getInitialSelectedOptionsForProduct = (product: Product | Bundle, sectionData: TPDPSectionData) => {
  let defaultSelectedOptions = []
  // For now not keeping color dependency here.
  if (isBrowser()) {
    const colorOption = getOptionBasedOnQueryString(product, COLOR_QUERY_STRING, ProductOptionName.Color)
    if (colorOption) {
      defaultSelectedOptions.push(colorOption)
    }
    const sizeOption = getOptionBasedOnQueryString(product, SIZE_QUERY_STRING, ProductOptionName.Size)
    if (sizeOption) {
      defaultSelectedOptions.push(sizeOption)
    }
    const productOptionNames = getProductOptionNamesFromProduct(product)
    productOptionNames.forEach(name => {
      const option = getOptionBasedOnQueryString(product, name.toLowerCase(), name as ProductOptionName)
      if (option) {
        defaultSelectedOptions.push(option)
      }
    })
  } else {
    defaultSelectedOptions = sectionData.selectedOptions
  }

  return getInitialSelectedOptions(product, defaultSelectedOptions, false, false, isNestedProduct(product) ? 2 : 1)
}

export default ProductPurchase
